import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../../config';
import ChatMessageList from './ChatMessageList';
import ChatComposer from './ChatComposer';

async function fetchJson(url, init) {
  const res = await fetch(url, init);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Request failed');
  return data;
}

async function streamNdjson(url, init, onEvent) {
  const res = await fetch(url, init);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || 'Request failed');
  }
  const reader = res.body?.getReader?.();
  if (!reader) throw new Error('Streaming not supported');
  const decoder = new TextDecoder('utf-8');
  let buf = '';
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, idx).trim();
      buf = buf.slice(idx + 1);
      if (!line) continue;
      const evt = JSON.parse(line);
      onEvent?.(evt);
    }
  }
}

export default function CoachChatPanel({ isActive, planId, canEdit, lang, onPlanChanged, plan = null }) {
  const { t, i18n } = useTranslation();
  const effectiveLang = lang || i18n.language || 'en';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [rateLimitedUntilMs, setRateLimitedUntilMs] = useState(null);
  const [rateLimitMeta, setRateLimitMeta] = useState(null); // { reset_at, limit, window_minutes }

  const canUseChat = Boolean(canEdit && planId);

  const loadConversation = useCallback(async () => {
    if (!canUseChat) return;
    setLoading(true);
    setError(null);
    try {
      const list = await fetchJson(`${API_BASE}/conversations?plan_id=${encodeURIComponent(planId)}`, {
        credentials: 'include'
      });
      const conv = Array.isArray(list) && list.length > 0 ? list[0] : null;
      const created = !conv
        ? await fetchJson(`${API_BASE}/conversations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ plan_id: planId })
          })
        : conv;

      const convId = created?.id ? String(created.id) : null;
      if (!convId) throw new Error('Failed to create conversation');
      setConversationId(convId);

      const payload = await fetchJson(`${API_BASE}/conversations/${convId}?limit=60`, { credentials: 'include' });
      setMessages(Array.isArray(payload?.messages) ? payload.messages : []);
    } catch (e) {
      setError(e?.message || 'Failed to load chat');
    } finally {
      setLoading(false);
    }
  }, [canUseChat, planId]);

  useEffect(() => {
    if (!isActive) return;
    setDraft('');
    loadConversation();
  }, [isActive, loadConversation]);

  const sendMessage = async () => {
    if (!conversationId) return;
    if (rateLimitedUntilMs && Date.now() < rateLimitedUntilMs) return;
    const content = String(draft || '').trim();
    if (!content) return;

    setSending(true);
    setError(null);
    try {
      setDraft('');
      const optimisticUser = { id: `tmp-user-${Date.now()}`, role: 'user', content, meta_json: { lang: effectiveLang }, created_at: new Date().toISOString() };
      const optimisticAssistantId = `tmp-assistant-${Date.now()}`;
      const optimisticAssistant = { id: optimisticAssistantId, role: 'assistant', content: '', meta_json: { lang: effectiveLang, streaming: true }, created_at: new Date().toISOString() };
      setMessages((prev) => [...prev, optimisticUser, optimisticAssistant]);

      let finalAssistant = null;
      let realUser = null;

      await streamNdjson(`${API_BASE}/conversations/${conversationId}/messages/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content, lang: effectiveLang })
      }, (evt) => {
        if (evt?.type === 'rate_limited') {
          const resetAt = evt?.reset_at ? new Date(String(evt.reset_at)) : null;
          const untilMs = resetAt && !Number.isNaN(resetAt.getTime())
            ? resetAt.getTime()
            : (Date.now() + Number(evt?.retry_after_seconds || 60) * 1000);
          setRateLimitedUntilMs(untilMs);
          setRateLimitMeta({
            reset_at: resetAt ? resetAt.toISOString() : null,
            limit: evt?.limit ?? null,
            window_minutes: evt?.window_minutes ?? null
          });
          // Rollback optimistic messages
          setMessages((prev) => prev.filter((m) => String(m.id) !== String(optimisticUser.id) && String(m.id) !== String(optimisticAssistantId)));
          const err = new Error('rate_limited');
          err.code = 'rate_limited';
          err.reset_at = evt?.reset_at || null;
          err.retry_after_seconds = evt?.retry_after_seconds || null;
          err.limit = evt?.limit || null;
          err.window_minutes = evt?.window_minutes || null;
          throw err;
        }
        if (evt?.type === 'user_message' && evt.user_message) {
          realUser = evt.user_message;
          setMessages((prev) => {
            const next = [...prev];
            const idx = next.findIndex((m) => String(m.id || '') === String(optimisticUser.id));
            if (idx >= 0) next[idx] = realUser;
            return next;
          });
        }

        if (evt?.type === 'delta' && typeof evt.delta === 'string') {
          const delta = evt.delta;
          setMessages((prev) => prev.map((m) => (
            String(m.id) === String(optimisticAssistantId)
              ? { ...m, content: String(m.content || '') + delta }
              : m
          )));
        }

        if (evt?.type === 'final' && evt.assistant_message) {
          finalAssistant = evt.assistant_message;
          setMessages((prev) => prev.map((m) => (
            String(m.id) === String(optimisticAssistantId)
              ? finalAssistant
              : m
          )));
        }

        if (evt?.type === 'error') {
          throw new Error(evt.error || 'Stream error');
        }
      });

      if (!finalAssistant) throw new Error('No assistant reply');
    } catch (e) {
      if (e?.code === 'rate_limited') {
        // Do not set fatal error UI.
      } else {
        setError(e?.message || 'Failed to send');
      }
    } finally {
      setSending(false);
    }
  };

  const applyProposal = async (proposal, ctx) => {
    const kind = String(proposal?.kind || '');
    if (!planId) return;
    const sourceMessageId = ctx?.messageId ? String(ctx.messageId) : null;

    const markAction = async (action) => {
      if (!conversationId || !sourceMessageId) return;
      await fetchJson(`${API_BASE}/conversations/${conversationId}/messages/${sourceMessageId}/proposal-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action })
      });
      setMessages((prev) => prev.map((m) => (
        String(m.id) === sourceMessageId
          ? { ...m, meta_json: { ...(m.meta_json || {}), proposal_action: action } }
          : m
      )));
    };

    if (kind === 'workout_patches') {
      const modifications = proposal?.modifications || null;
      if (!modifications || typeof modifications !== 'object') throw new Error('Invalid workout patches');
      const payload = {
        modifications,
        request_text: proposal?.request_text || 'Coach chat proposal',
        analysis: proposal?.analysis || { analysis: proposal?.rationale || '', suggestions: [] }
      };
      await fetchJson(`${API_BASE}/plans/${planId}/apply-workout-patches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      await markAction('applied');
      onPlanChanged?.();
      return;
    }

    if (kind === 'plan_changes') {
      const changes = Array.isArray(proposal?.changes) ? proposal.changes : null;
      if (!changes || changes.length === 0) throw new Error(t('chat.proposal.invalid_changes'));
      // Let the backend normalise the proposal into an apply-changes payload, then apply it.
      let payload = null;
      if (conversationId) {
        const ready = await fetchJson(`${API_BASE}/conversations/${conversationId}/apply-proposal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ proposal })
        });
        payload = ready?.payload || null;
      }
      if (!payload) {
        payload = {
          changes,
          request_text: proposal?.request_text || 'Coach chat proposal',
          analysis: { analysis: proposal?.rationale || '', source: 'chat' }
        };
      }
      await fetchJson(`${API_BASE}/plans/${planId}/apply-changes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      await markAction('applied');
      onPlanChanged?.();
      return;
    }

    if (kind === 'plan_regeneration') {
      const modifications = proposal?.modifications || null;
      if (!modifications || typeof modifications !== 'object') throw new Error('Invalid plan modifications');
      const payload = {
        modifications,
        request_text: proposal?.request_text || 'Coach chat proposal',
        analysis: proposal?.analysis || { analysis: proposal?.rationale || '', suggestions: [] }
      };
      await fetchJson(`${API_BASE}/plans/${planId}/apply-modification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      await markAction('applied');
      onPlanChanged?.();
      return;
    }

    throw new Error('Unsupported proposal kind');
  };

  const ignoreProposal = async (_proposal, ctx) => {
    const sourceMessageId = ctx?.messageId ? String(ctx.messageId) : null;
    if (!conversationId || !sourceMessageId) return;
    await fetchJson(`${API_BASE}/conversations/${conversationId}/messages/${sourceMessageId}/proposal-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action: 'ignored' })
    });
    setMessages((prev) => prev.map((m) => (
      String(m.id) === sourceMessageId
        ? { ...m, meta_json: { ...(m.meta_json || {}), proposal_action: 'ignored' } }
        : m
    )));
  };

  if (!canUseChat) {
    return <div className="p-4 text-sm text-gray-700">{t('chat.disabled')}</div>;
  }

  if (loading) {
    return <div className="p-4 text-sm text-gray-700">{t('chat.loading')}</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-sm text-red-600 font-bold">{error}</div>
        <button
          type="button"
          onClick={loadConversation}
          className="mt-3 px-4 py-2 rounded-lg bg-gray-100 text-gray-800 font-extrabold hover:bg-gray-200"
        >
          {t('chat.retry')}
        </button>
      </div>
    );
  }

  const blocked = Boolean(sending || (rateLimitedUntilMs && Date.now() < rateLimitedUntilMs));
  const resetTimeText = (() => {
    if (!rateLimitedUntilMs) return null;
    const d = new Date(rateLimitedUntilMs);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  })();

  return (
    <div className="h-full min-h-0 flex flex-col">
      <ChatMessageList
        messages={messages}
        onApplyProposal={applyProposal}
        onIgnoreProposal={ignoreProposal}
        plan={plan}
      />
      {rateLimitedUntilMs && Date.now() < rateLimitedUntilMs ? (
        <div className="border-t border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
          <div className="font-extrabold">{t('chat.rate_limit.title')}</div>
          <div className="text-[12px] mt-0.5">
            {t('chat.rate_limit.message', {
              limit: rateLimitMeta?.limit ?? 10,
              window: rateLimitMeta?.window_minutes ?? 60,
              time: resetTimeText || '—'
            })}
          </div>
        </div>
      ) : null}
      <ChatComposer
        value={draft}
        onChange={setDraft}
        onSend={sendMessage}
        disabled={blocked}
      />
    </div>
  );
}


